import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Package, Factory, Wheat, Edit, History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

// Reason codes for inventory edits
const REASON_CODES = [
  'Sale adjustment',
  'Production added',
  'Damage/Spoilage',
  'Count correction',
  'Transfer',
  'Other'
];

// Standard byproduct categories
const BYPRODUCT_CATEGORIES = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function Inventory() {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    factory: '',
    product_type: '',
    pack_size_id: '',
    byproduct_name: '',
    current_value: 0,
    new_value: '',
    reason: '',
    notes: ''
  });
  const [editLabel, setEditLabel] = useState('');
  
  // History Dialog State
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [historyLabel, setHistoryLabel] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await api.get('/inventory');
      setInventory(res.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat('en-KE').format(num);
  };

  const openEditDialog = (factory, productType, packSizeId = null, byproductName = null, currentValue = 0, label = '') => {
    setEditData({
      factory,
      product_type: productType,
      pack_size_id: packSizeId,
      byproduct_name: byproductName,
      current_value: currentValue,
      new_value: currentValue.toString(),
      reason: '',
      notes: ''
    });
    setEditLabel(label);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editData.reason) {
      toast.error('Please select a reason for this edit');
      return;
    }
    
    if (editData.reason === 'Other' && !editData.notes) {
      toast.error('Notes required for "Other" reason');
      return;
    }
    
    try {
      await api.put('/inventory/edit', {
        factory: editData.factory,
        product_type: editData.product_type,
        pack_size_id: editData.pack_size_id,
        byproduct_name: editData.byproduct_name,
        new_value: parseFloat(editData.new_value) || 0,
        reason: editData.reason,
        notes: editData.notes
      });
      toast.success('Inventory updated');
      setEditDialogOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
    }
  };

  const openHistoryDialog = async (factory, productType, packSizeId = null, byproductName = null, label = '') => {
    try {
      let url = `/inventory/edit-history/${factory}/${productType}`;
      if (packSizeId) url += `?pack_size_id=${packSizeId}`;
      if (byproductName) url += `?byproduct_name=${byproductName}`;
      
      const res = await api.get(url);
      setEditHistory(res.data.edit_history || []);
      setHistoryLabel(label);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load edit history');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Inventory (Manual Control)</h1>
      </div>

      <Tabs defaultValue="finished" className="space-y-4">
        <TabsList>
          <TabsTrigger value="finished" className="flex items-center gap-2">
            <Package className="h-4 w-4" /> Finished Goods
          </TabsTrigger>
          <TabsTrigger value="raw" className="flex items-center gap-2">
            <Wheat className="h-4 w-4" /> Raw Maize
          </TabsTrigger>
          <TabsTrigger value="byproducts" className="flex items-center gap-2">
            <Factory className="h-4 w-4" /> Byproducts
          </TabsTrigger>
        </TabsList>

        {/* Finished Goods Tab */}
        <TabsContent value="finished">
          <Card>
            <CardHeader>
              <CardTitle>Finished Goods by Factory</CardTitle>
              <p className="text-sm text-slate-500">Click Edit to set exact quantity with reason code</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pack Size</TableHead>
                    <TableHead className="text-center">Ndumberi</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                    <TableHead className="text-center">Githunguri</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                    <TableHead className="text-right font-bold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory?.finished_goods_by_factory?.map((fg, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{fg.pack_size_kg}kg</TableCell>
                      <TableCell className="text-center text-blue-700 font-semibold">
                        {formatNumber(fg.ndumberi_bales)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog('Ndumberi', 'finished_goods', fg.pack_size_id, null, fg.ndumberi_bales, `${fg.pack_size_kg}kg @ Ndumberi`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center text-emerald-700 font-semibold">
                        {formatNumber(fg.githunguri_bales)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog('Githunguri', 'finished_goods', fg.pack_size_id, null, fg.githunguri_bales, `${fg.pack_size_kg}kg @ Githunguri`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(fg.total_bales)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-center text-blue-700">
                      {formatNumber(inventory?.finished_goods_by_factory?.reduce((sum, fg) => sum + (fg.ndumberi_bales || 0), 0))}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-center text-emerald-700">
                      {formatNumber(inventory?.finished_goods_by_factory?.reduce((sum, fg) => sum + (fg.githunguri_bales || 0), 0))}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right">
                      {formatNumber(inventory?.finished_goods_total || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Raw Maize Tab */}
        <TabsContent value="raw">
          <Card>
            <CardHeader>
              <CardTitle>Raw Maize by Factory</CardTitle>
              <p className="text-sm text-slate-500">Click Edit to set exact quantity with reason code</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Factory</TableHead>
                    <TableHead className="text-right">Quantity (kg)</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Ndumberi</TableCell>
                    <TableCell className="text-right text-blue-700 font-semibold">
                      {formatNumber(inventory?.raw_maize_by_factory?.ndumberi_kg || 0)} kg
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog('Ndumberi', 'raw_maize', null, null, inventory?.raw_maize_by_factory?.ndumberi_kg || 0, 'Raw Maize @ Ndumberi')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Githunguri</TableCell>
                    <TableCell className="text-right text-emerald-700 font-semibold">
                      {formatNumber(inventory?.raw_maize_by_factory?.githunguri_kg || 0)} kg
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog('Githunguri', 'raw_maize', null, null, inventory?.raw_maize_by_factory?.githunguri_kg || 0, 'Raw Maize @ Githunguri')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">
                      {formatNumber(inventory?.raw_maize_by_factory?.total_kg || 0)} kg
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Byproducts Tab */}
        <TabsContent value="byproducts">
          <Card>
            <CardHeader>
              <CardTitle>Byproducts by Factory</CardTitle>
              <p className="text-sm text-slate-500">Click Edit to set exact quantity with reason code</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Ndumberi (kg)</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                    <TableHead className="text-center">Githunguri (kg)</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                    <TableHead className="text-right font-bold">Total (kg)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory?.byproducts_by_factory?.map((bp, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{bp.category}</TableCell>
                      <TableCell className="text-center text-blue-700 font-semibold">
                        {formatNumber(bp.ndumberi_kg)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog('Ndumberi', 'byproduct', null, bp.category, bp.ndumberi_kg, `${bp.category} @ Ndumberi`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center text-emerald-700 font-semibold">
                        {formatNumber(bp.githunguri_kg)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog('Githunguri', 'byproduct', null, bp.category, bp.githunguri_kg, `${bp.category} @ Githunguri`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(bp.total_kg)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-center text-blue-700">
                      {formatNumber(inventory?.byproducts_by_factory?.reduce((sum, bp) => sum + (bp.ndumberi_kg || 0), 0))}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-center text-emerald-700">
                      {formatNumber(inventory?.byproducts_by_factory?.reduce((sum, bp) => sum + (bp.githunguri_kg || 0), 0))}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right">
                      {formatNumber(inventory?.byproducts_total || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Inventory Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="font-medium">{editLabel}</p>
              <p className="text-sm text-slate-600">Current: {formatNumber(editData.current_value)} {editData.product_type === 'finished_goods' ? 'bales' : 'kg'}</p>
            </div>
            
            <div className="space-y-2">
              <Label>New Value *</Label>
              <Input
                type="number"
                min="0"
                step={editData.product_type === 'finished_goods' ? '1' : '0.1'}
                value={editData.new_value}
                onChange={(e) => setEditData({...editData, new_value: e.target.value})}
                placeholder="Enter exact value"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason Code *</Label>
              <Select value={editData.reason} onValueChange={(v) => setEditData({...editData, reason: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason for edit" />
                </SelectTrigger>
                <SelectContent>
                  {REASON_CODES.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes {editData.reason === 'Other' ? '*' : '(Optional)'}</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({...editData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            
            <Button type="submit" className="w-full btn-primary">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit History - {historyLabel}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {editHistory.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Previous</TableHead>
                    <TableHead>New</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editHistory.map((h, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs">{h.edited_at?.substring(0,10)}</TableCell>
                      <TableCell>{formatNumber(h.previous_value)}</TableCell>
                      <TableCell>{formatNumber(h.new_value)}</TableCell>
                      <TableCell>{h.reason}</TableCell>
                      <TableCell className="text-xs">{h.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-slate-500 py-8">No edit history</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
